'use strict';

const { BaseWidget } = require('./BaseWidget');
const { h } = require('./dom');
const { BX } = require('./theme');
const { formatCurrency } = require('./format');

function genOrderId() {
  return `ORD${Date.now()}${Math.floor(Math.random() * 1000)}`;
}

function pick(obj, ...keys) {
  for (const k of keys) {
    if (obj && obj[k] != null && obj[k] !== '') return obj[k];
  }
  return undefined;
}

const STATUS_ICON = {
  SUCCESS: { icon: '✓', bg: BX.success2 },
  PENDING: { icon: '⏳', bg: BX.warning },
  FAILED: { icon: '✕', bg: BX.error },
};

/**
 * UpiWidget — QR collection (static or amount-specific dynamic). QR
 * generation mirrors payapp's actual flow exactly: one endpoint
 * (client.upi.generateQr, POST /upi/txn/v1/GenerateQR) handles both —
 * omitting `amount` gets a static QR (the terminal's fixed VPA), passing it
 * gets a dynamic, amount-specific one. Whether `start()` is called with an
 * amount decides which one you get: pass it to go straight to a dynamic QR
 * (no menu shown, matching "the SDK decides from the caller's own params");
 * omit it to let the operator choose Static QR / Dynamic QR from a menu.
 *
 * "Collect via VPA" is disabled in this menu (client.upi.collect() is still
 * available directly if you want to build your own VPA flow).
 *
 * const widget = new UpiWidget(client, { onSuccess, onError });
 * widget.mount('#bbx-upi').start({ amount: 499 }); // -> straight to dynamic QR
 * widget.mount('#bbx-upi').start();                // -> menu (static/dynamic)
 */
class UpiWidget extends BaseWidget {
  start(params = {}) {
    this._params = { orderId: genOrderId(), ...params };
    this._pollTimer = null;

    const amt = Number(this._params.amount);
    if (amt > 0) {
      this._params.amount = amt;
      this.generateQr();
    } else {
      this._params.amount = undefined;
      this.screenMenu();
    }
    return this;
  }

  unmount() {
    if (this._pollTimer) clearInterval(this._pollTimer);
    super.unmount();
  }

  // ── Menu (only shown when no amount was given to start()) ─────────

  screenMenu() {
    this.setHeader({ title: 'UPI Payment', subtitle: 'Choose how to collect', showBack: false });

    // Collect via VPA is disabled — only Static/Dynamic QR are offered.
    const items = [
      { id: 'static', icon: '▦', label: 'Static QR', onClick: () => { this._params.amount = undefined; this.generateQr(); } },
      { id: 'dynamic', icon: '#', label: 'Dynamic QR', onClick: () => this.screenAmountEntry('dynamic') },
    ];

    const grid = h('div', { class: 'bbx-select-grid' }, items.map((item) =>
      h('div', { class: 'bbx-select-item', onClick: item.onClick }, [
        h('div', { class: 'bbx-sel-icon', text: item.icon }),
        h('div', { class: 'bbx-sel-label', text: item.label }),
      ]),
    ));

    this.showScreen(h('div', {}, [grid]));
  }

  /** Amount entry for the two flows that need one — Dynamic QR and VPA
   *  collect. Static QR never reaches this screen. */
  screenAmountEntry(next, errorMsg) {
    this.setHeader({ title: next === 'vpa' ? 'Collect via VPA' : 'Dynamic QR', subtitle: 'Enter the amount to collect', showBack: true, onBack: () => this.screenMenu() });

    const amountField = this.field({ label: 'Amount', type: 'number', placeholder: '0.00', value: this._params.amount ? String(this._params.amount) : '', onInput: (v) => { this._params.amount = v; } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        amountField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn',
          text: 'Continue',
          onClick: () => {
            const amt = Number(this._params.amount);
            if (!amt || amt <= 0) {
              errorSlot.replaceChildren(this.banner('error', 'Enter a valid amount'));
              return;
            }
            this._params.amount = amt;
            if (next === 'vpa') this.screenVpa();
            else this.generateQr();
          },
        }),
      ]),
    );
  }

  // ── QR generation (static when amount is unset, dynamic when set) ──

  async generateQr() {
    const isStatic = !this._params.amount;
    this.setHeader({ title: isStatic ? 'Scan to Pay' : 'Scan to Pay', subtitle: isStatic ? 'Static QR' : formatCurrency(this._params.amount), showBack: true, onBack: () => this.screenMenu() });
    this.renderLoading('Generating QR…');

    try {
      // amount omitted entirely for a static QR (not sent as 0) — matches
      // isg.txn.controller.js#ISGgenerateQR's documented contract exactly:
      // "amount: optional for StaticQR / required for DynamicQR".
      const res = await this.client.upi.generateQr({
        amount: this._params.amount || undefined,
        order_id: this._params.orderId,
      });

      if (res.statusCode !== '00') {
        this.showScreen(this.banner('error', res.message || 'Unable to generate QR'));
        this.fail(res, res.message);
        return;
      }

      // data.qr_image is a ready base64 PNG data URL rendered server-side
      // (QRCode.toDataURL(result.data.qr_string)) — no client-side QR
      // rendering needed. Fall back to qrString/an external renderer only
      // if a differently-shaped response ever comes back without it.
      const qrImgSrc = res.data?.qr_image
        || (res.data?.qrString ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(res.data.qrString)}` : null);

      this._params.txnRefNo = res.data?.txn_ref_no;

      const screen = h('div', {}, [
        h('div', { class: 'bbx-qr-box' }, qrImgSrc ? [h('img', { src: qrImgSrc, alt: 'UPI QR' })] : [this.banner('warning', 'QR generated — waiting for payment app data')]),
        h('p', { class: 'bbx-muted', style: { textAlign: 'center' }, text: isStatic ? 'Scan to pay any amount' : 'Waiting for payment…' }),
        h('a', { class: 'bbx-link', text: 'Back', onClick: () => { this.stopPolling(); this.screenMenu(); } }),
      ]);
      this.showScreen(screen);

      // Static QR has no fixed amount/order to poll a single outcome for —
      // it's scan-any-time, not a one-shot payment request.
      if (!isStatic && this._params.txnRefNo) this.startPolling(this._params.txnRefNo);
    } catch (err) {
      this.showScreen(this.banner('error', this.errorMessage(err, 'Unable to generate QR')));
      this.fail(err);
    }
  }

  startPolling(transId) {
    this.stopPolling();
    const startedAt = Date.now();
    this._pollTimer = setInterval(async () => {
      if (Date.now() - startedAt > 3 * 60 * 1000) {
        this.stopPolling();
        this.screenResult({ statusCode: '03', message: 'Payment timed out', status: 'FAILED' });
        return;
      }
      try {
        const res = await this.client.upi.upiStatus({ trans_id: transId });
        // UpiStatus's statusCode IS the state, not just a call-succeeded flag:
        // '00' processed/done, '01' pending (keep polling), '03' failed.
        if (res.statusCode === '00') {
          this.stopPolling();
          this.screenResult({ ...res, status: 'SUCCESS' });
        } else if (res.statusCode === '03') {
          this.stopPolling();
          this.screenResult({ ...res, status: 'FAILED' });
        }
        // '01' (or anything else, defensively) — still pending, keep polling.
      } catch (e) {
        // transient — keep polling until timeout
      }
    }, 3000);
  }

  stopPolling() {
    if (this._pollTimer) clearInterval(this._pollTimer);
    this._pollTimer = null;
  }

  // ── VPA collect path ─────────────────────────────────────────────

  screenVpa(errorMsg) {
    this.setHeader({ title: 'Collect via VPA', subtitle: formatCurrency(this._params.amount), showBack: true, onBack: () => this.screenAmountEntry('vpa') });

    const vpaField = this.field({ label: 'Customer VPA', placeholder: 'name@bank', value: this._params.vpa || '', onInput: (v) => { this._params.vpa = v; } });
    const nameField = this.field({ label: 'Customer Name (optional)', value: this._params.customerName || '', onInput: (v) => { this._params.customerName = v; } });
    const mobileField = this.field({ label: 'Customer Mobile (optional)', inputmode: 'numeric', value: this._params.customerMobile || '', onInput: (v) => { this._params.customerMobile = v; } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        vpaField.node,
        nameField.node,
        mobileField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn',
          text: 'Send Collect Request',
          onClick: async (e) => {
            if (!this._params.vpa) {
              errorSlot.replaceChildren(this.banner('error', 'Enter the customer’s VPA'));
              return;
            }
            e.target.disabled = true;
            this.showOverlay('Sending collect request…');
            try {
              const res = await this.client.upi.collect({
                amount: this._params.amount,
                order_id: this._params.orderId,
                vpa: this._params.vpa,
                customer_name: this._params.customerName,
                customer_mobile: this._params.customerMobile,
                remarks: this._params.remarks,
              });
              this.hideOverlay();
              this.screenResult({ ...res, status: res.statusCode === '00' ? 'SUCCESS' : 'FAILED' });
            } catch (err) {
              this.hideOverlay();
              this.fail(err);
              this.screenVpa(this.errorMessage(err, 'Collect request failed'));
            }
          },
        }),
      ]),
    );
  }

  // ── Result / transaction details ──────────────────────────────────

  screenResult(res) {
    this.setHeader({ title: 'Transaction', showBack: false });
    const meta = STATUS_ICON[res.status] || STATUS_ICON.PENDING;
    const title = res.status === 'SUCCESS' ? 'Payment Successful' : res.status === 'FAILED' ? 'Payment Failed' : 'Payment Pending';
    const d = res.data || {};

    const rows = [
      h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Order ID' }), h('span', { class: 'bbx-row-v bbx-mono', text: this._params.orderId })]),
      pick(d, 'txn_ref_no', 'rrn', 'trans_id') ? h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Reference' }), h('span', { class: 'bbx-row-v bbx-mono', text: String(pick(d, 'txn_ref_no', 'rrn', 'trans_id')) })]) : null,
      pick(d, 'payerVpa', 'payer_virtual_address') ? h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Paid By' }), h('span', { class: 'bbx-row-v bbx-mono', text: String(pick(d, 'payerVpa', 'payer_virtual_address')) })]) : null,
      pick(d, 'bankReference', 'bank_reference') ? h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Bank Reference' }), h('span', { class: 'bbx-row-v bbx-mono', text: String(pick(d, 'bankReference', 'bank_reference')) })]) : null,
    ].filter(Boolean);

    // succeed()/fail() fire when the user acknowledges the result (clicks
    // Done), not the instant it's known — firing immediately here meant a
    // caller whose onSuccess/onError closes the widget (the documented
    // Checkout/bucksboxHandler pattern does exactly that) would tear it down
    // before the customer ever saw this screen. Full transaction details
    // (whatever the status response returned in `data`) go back to the page
    // that started the widget via onSuccess — not just the reference number
    // shown on screen.
    const payload = { ...res, orderId: this._params.orderId, amount: this._params.amount };
    const reportResult = () => {
      if (res.status === 'SUCCESS') this.succeed(payload);
      else if (res.status === 'FAILED') this.fail(res, res.message);
    };

    this.showScreen(
      h('div', {}, [
        h('div', { class: 'bbx-status-icon', style: { background: meta.bg }, text: meta.icon }),
        h('p', { class: 'bbx-status-title', text: title }),
        res.message ? h('p', { class: 'bbx-status-sub', text: res.message }) : null,
        h('div', { class: 'bbx-card' }, [
          h('div', { class: 'bbx-amount-hero', text: formatCurrency(pick(d, 'amount') ?? this._params.amount) }),
          ...rows,
        ]),
        h('button', { class: 'bbx-btn', text: 'Done', onClick: () => { reportResult(); this.opts.onClose && this.opts.onClose(); } }),
      ]),
    );
  }
}

module.exports = { UpiWidget };
